import type { BasketItemFlags } from '@virginexperiencedays/basket/src/utils/basketItemFlags';
import { useFeatureFlagsServer } from '@virginexperiencedays/feature-flags/src/useFlags';
import type { Product, Review } from '@virginexperiencedays/products';
import type { ProductPromo } from '@virginexperiencedays/promos';
import type { ReactNode } from 'react';
import type { PdpInteractionProps } from '../../../libs/tracking/pdp';
import { initAlgoliaTrackingClient } from '../../../utils/algolia/tracking';
import { getFullSku, getProductUrl } from '../../../utils/product';
import {
  pageView as trackFbPageView,
  viewContent as trackFbViewContent,
} from '../../tracking/fb/events';
import { GLOBAL_SETTINGS } from '../../tracking/gtm/consts';
import {
  trackViewedItem as trackKlaviyoViewedItem,
  trackViewedProduct as trackKlaviyoViewedProduct,
} from '../../tracking/klaviyo';
import { useTracking } from '../../tracking/useTracking';
import ControlPage from './ControlPage';
import { Page as VariationPage } from './VariationPage';
import type { Reviews as FilteredReviews } from './VariationPage/extractors/reviewsExtractor';
import { productView } from './tracking/gtm';

export type PageProps = {
  product: Product | null;
  reviews: Review[] | null;
  isActive: boolean;
  productPromo: ProductPromo | null;
  collectionSlug?: string;
  slugNameMap?: Record<string, string>;
  slugBreadcrumbNameMap?: Record<string, string>;
  trackingPageType: string;
  // ? variationData?: Record<string, any>; - only required with active split test
};

export type PageUIWrapperProps = {
  children: ReactNode;
  isCollectionChoice: boolean;
  isCollectionPages: boolean;
  isCollectionProduct: boolean;
  lvl0: string;
  lvl1: string;
  lvl2: string;
  product: Product;
  promocode: string;
  quickBasketOpen: boolean;
  reviewsList: Review[];
  seoPathOverride: string;
  sku: string;
  slugBreadcrumbNameMap: Record<string, string>;
  breadcrumbs: (lvl: string, nameMap: Record<string, string>) => { name: string }[];
  handleQuickBasketOpen: (isOpen: boolean) => void;
  handleUpdateBasketCount: (n: number) => void;
  setQuickBasketOpen: (isOpen: boolean) => void;
};

export type PageUIProps = {
  product: Product;
  productPromo: ProductPromo | null;
  isActive: boolean;
  reviews: FilteredReviews;
  flags?: BasketItemFlags[];
  isCollectionProduct?: boolean;
  handleQuickBasket?: (boolean: boolean) => void;
  slugNameMap?: Record<string, string>;
  trackingData?: PdpInteractionProps;
};

export default function Page(props: PageProps) {
  const [isEnabledServer] = useFeatureFlagsServer();

  const { isCollectionChoice, isCollectionProduct } = props.product ?? {};
  const newPdpEnabled = isEnabledServer('FF_new_pdp');
  const newCollectionsPdpEnabled = isEnabledServer('FF_new_pdp_collections');

  // tracking
  usePdpTracking({ product: props.product });

  // never display new PDP for collection choice products (not a valid page type in new UI)
  if (isCollectionChoice) return <ControlPage {...props} />;

  // if a collection product, and all FF's enabled, display the new UI
  if (isCollectionProduct && newPdpEnabled && newCollectionsPdpEnabled) {
    return <VariationPage {...props} />;
  }

  // if not a collection product, and PDP FF enabled, display the new UI
  if (!isCollectionProduct && newPdpEnabled) return <VariationPage {...props} />;

  // fallback to display control UI
  return <ControlPage {...props} />;
}

const usePdpTracking = ({ product }: { product: Product | null }) => {
  const { title, categoryHierarchy } = product || {};
  const { lvl0 } = categoryHierarchy ?? {};

  initAlgoliaTrackingClient();

  const handleFbTracking = ({ name, category }: { name: string; category: string }) => {
    trackFbPageView({ name });
    trackFbViewContent({ name, category });
  };

  const legacyTrackingData = {
    pageName: title,
    pageType: 'Product',
    categoryName: '',
  };

  const gtmTrackingData = {
    initial: [
      {
        ...GLOBAL_SETTINGS,
        br_ptype: 'product',
        pageType: 'Product Page',
      },
      { ecommerce: null },
      productView(product),
    ],
  };

  const klaviyoTrackingData = {
    title: product?.title ?? '',
    slug: product?.slug ?? '',
    sku: getFullSku({ sku: product?.sku ?? '', promocode: product?.promocode ?? '' }),
    categories: product?.categories ?? [],
    rrp: product?.price?.rrp ?? 0,
    displayPrice: product?.price?.displayPrice ?? 0,
    imageUrl: product?.media?.mainImage?.url ?? '',
    url: getProductUrl({
      slug: product?.slug ?? '',
      isCollectionProduct: product?.isCollectionProduct ?? false,
    }),
    isCollectionProduct: product?.isCollectionProduct ?? false,
  };

  useTracking({
    legacy: legacyTrackingData,
    gtm: gtmTrackingData,
    fb: () => handleFbTracking({ name: title ?? '', category: lvl0 ?? '' }),
    klaviyo: () => {
      trackKlaviyoViewedProduct?.(klaviyoTrackingData);
      trackKlaviyoViewedItem?.(klaviyoTrackingData);
    },
  });
};
